import React, { useState, useEffect } from 'react';
import {
  Calendar,
  AlertCircle,
  Clock,
  CheckCircle,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ChevronUp,
  Wallet,
  ArrowUpCircle,
  ArrowDownCircle,
  FileText,
} from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';
import { Movimento, ScadenzeOverview } from '@/types';
import scadenzeService from '@/services/scadenzeService';
import styles from './ScadenzePage.module.css';
import { ExportDropdown, ExportFormat } from '@/components/common/ExportDropdown';
import { exportToExcel, exportToPdf, ExportColumn, SummaryRow } from '@/utils/exports';

export const ScadenzePage: React.FC = () => {
  const [overview, setOverview] = useState<ScadenzeOverview | null>(null);
  const [scadute, setScadute] = useState<Movimento[]>([]);
  const [prossime, setProssime] = useState<Movimento[]>([]);
  const [programmate, setProgrammate] = useState<Movimento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);

  // Sections expanded state
  const [scaduteExpanded, setScaduteExpanded] = useState(true);
  const [prossimeExpanded, setProssimeExpanded] = useState(true);
  const [programmateExpanded, setProgrammateExpanded] = useState(true);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [overviewData, scaduteData, prossimeData, programmateData] = await Promise.all([
        scadenzeService.getOverview(),
        scadenzeService.getScadute(),
        scadenzeService.getProssime(30),
        scadenzeService.getProgrammate(),
      ]);

      setOverview(overviewData);
      setScadute(scaduteData);
      setProssime(prossimeData);
      setProgrammate(programmateData);
    } catch (err) {
      console.error('Error loading scadenze:', err);
      setError('Errore nel caricamento delle scadenze');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleMarkAsPaid = async (id: number) => {
    try {
      await scadenzeService.markAsPaid(id);
      await loadData(); // Reload all data
    } catch (err) {
      console.error('Error marking as paid:', err);
      alert('Errore nel segnare come pagato');
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getDaysUntil = (dateStr: string): number => {
    if (!dateStr) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateStr);
    date.setHours(0, 0, 0, 0);
    const diff = date.getTime() - today.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  // Calcola statistiche per la stats bar
  const allMovimenti = [...scadute, ...prossime, ...programmate];
  const stats = {
    totaleCount: allMovimenti.length,
    totaleImporto: allMovimenti.reduce((sum, m) => sum + m.importo, 0),
    daIncassare: allMovimenti.filter(m => m.tipo === 'entrata').reduce((sum, m) => sum + m.importo, 0),
    daPagare: allMovimenti.filter(m => m.tipo === 'uscita').reduce((sum, m) => sum + m.importo, 0),
  };

  // Handle export
  const handleExport = async (format: ExportFormat) => {
    if (allMovimenti.length === 0) {
      alert('Nessun dato da esportare');
      return;
    }

    setExportLoading(true);
    try {
      const columns: ExportColumn[] = [
        { field: 'dataScadenza', header: 'Data Scadenza', type: 'date', width: 12 },
        { field: 'tipo', header: 'Tipo', type: 'text', width: 10 },
        { field: 'descrizione', header: 'Descrizione', type: 'text', width: 25 },
        { field: 'contattoCategoria', header: 'Cliente/Categoria', type: 'text', width: 20 },
        { field: 'importo', header: 'Importo', type: 'currency', width: 12 },
        { field: 'rata', header: 'Rata', type: 'text', width: 10 },
        { field: 'stato', header: 'Stato', type: 'text', width: 15 },
      ];

      const getStato = (_m: Movimento, isScaduta: boolean, isProssima: boolean) => {
        if (isScaduta) return 'Scaduto';
        if (isProssima) return 'In Scadenza';
        return 'Programmato';
      };

      const exportData = [
        ...scadute.map(m => ({
          dataScadenza: m.dataScadenza || m.data,
          tipo: m.tipo === 'entrata' ? 'Entrata' : 'Uscita',
          descrizione: m.descrizione || m.note || '',
          contattoCategoria: m.contatto?.nome || m.categoria?.nome || '',
          importo: m.importo,
          rata: m.totaleRate && m.totaleRate > 1 ? `${m.numeroRata || 1}/${m.totaleRate}` : '',
          stato: getStato(m, true, false),
        })),
        ...prossime.map(m => ({
          dataScadenza: m.dataScadenza || m.data,
          tipo: m.tipo === 'entrata' ? 'Entrata' : 'Uscita',
          descrizione: m.descrizione || m.note || '',
          contattoCategoria: m.contatto?.nome || m.categoria?.nome || '',
          importo: m.importo,
          rata: m.totaleRate && m.totaleRate > 1 ? `${m.numeroRata || 1}/${m.totaleRate}` : '',
          stato: getStato(m, false, true),
        })),
        ...programmate.map(m => ({
          dataScadenza: m.dataScadenza || m.data,
          tipo: m.tipo === 'entrata' ? 'Entrata' : 'Uscita',
          descrizione: m.descrizione || m.note || '',
          contattoCategoria: m.contatto?.nome || m.categoria?.nome || '',
          importo: m.importo,
          rata: m.totaleRate && m.totaleRate > 1 ? `${m.numeroRata || 1}/${m.totaleRate}` : '',
          stato: getStato(m, false, false),
        })),
      ];

      const scaduteTotal = scadute.reduce((sum, m) => sum + m.importo, 0);
      const prossimeTotal = prossime.reduce((sum, m) => sum + m.importo, 0);
      const programmateTotal = programmate.reduce((sum, m) => sum + m.importo, 0);

      const summaryData: SummaryRow[] = [
        { label: 'Totale Scaduto', value: scaduteTotal, type: 'currency' },
        { label: 'Totale In Scadenza', value: prossimeTotal, type: 'currency' },
        { label: 'Totale Programmato', value: programmateTotal, type: 'currency' },
      ];

      const config = {
        title: 'Scadenze',
        filename: 'scadenze',
        columns,
        orientation: 'landscape' as const,
        summaryData,
      };

      if (format === 'excel') {
        await exportToExcel(config, exportData);
      } else {
        await exportToPdf(config, exportData);
      }
    } catch (err) {
      console.error('Error exporting scadenze:', err);
      alert('Errore durante l\'esportazione');
    } finally {
      setExportLoading(false);
    }
  };

  const getDaysBadge = (dateStr: string, isOverdue: boolean) => {
    const days = getDaysUntil(dateStr);
    if (isOverdue) {
      return <span className={`${styles.daysBadge} ${styles.overdue}`}>{Math.abs(days)} giorni fa</span>;
    }
    if (days === 0) {
      return <span className={`${styles.daysBadge} ${styles.today}`}>Oggi</span>;
    }
    if (days <= 7) {
      return <span className={`${styles.daysBadge} ${styles.soon}`}>{days} giorni</span>;
    }
    return <span className={`${styles.daysBadge} ${styles.future}`}>{days} giorni</span>;
  };

  const renderMovimentoRow = (movimento: Movimento, isOverdue: boolean = false) => {
    const isEntrata = movimento.tipo === 'entrata';
    return (
      <tr key={movimento.id} className={isOverdue ? styles.overdueRow : ''}>
        <td>
          <div className={styles.dateCell}>
            {formatDate(movimento.dataScadenza || movimento.data)}
            {getDaysBadge(movimento.dataScadenza || movimento.data, isOverdue)}
          </div>
        </td>
        <td>
          <span className={`${styles.tipoBadge} ${isEntrata ? styles.entrata : styles.uscita}`}>
            {isEntrata ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {isEntrata ? 'Entrata' : 'Uscita'}
          </span>
        </td>
        <td>{movimento.descrizione || movimento.note || '-'}</td>
        <td>{movimento.contatto?.nome || movimento.categoria?.nome || '-'}</td>
        <td className={isEntrata ? styles.importoEntrata : styles.importoUscita}>
          {isEntrata ? '+' : '-'}{formatCurrency(movimento.importo)}
        </td>
        <td>
          {movimento.numeroRata && movimento.totaleRate && (
            <span className={styles.rataBadge}>
              {movimento.numeroRata}/{movimento.totaleRate}
            </span>
          )}
        </td>
        <td>
          <button
            className={styles.btnPaga}
            onClick={() => handleMarkAsPaid(movimento.id)}
            title="Segna come pagato"
          >
            <CheckCircle size={16} />
            Paga
          </button>
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <RefreshCw className={styles.spinner} size={32} />
        <p>Caricamento scadenze...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <AlertCircle size={32} />
        <p>{error}</p>
        <button onClick={loadData} className={styles.btnRetry}>
          <RefreshCw size={16} />
          Riprova
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Calendar size={24} />
          <h1>Scadenze</h1>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <ExportDropdown
            onExport={handleExport}
            disabled={allMovimenti.length === 0}
            loading={exportLoading}
          />
          <button onClick={loadData} className={styles.btnRefresh}>
            <RefreshCw size={16} />
            Aggiorna
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className={styles.statsBar}>
        <div className={styles.statItem}>
          <FileText size={20} style={{ color: 'var(--primary-main)' }} />
          <span className={styles.statNumber}>{stats.totaleCount}</span>
          <span className={styles.statText}>Scadenze</span>
        </div>
        <div className={styles.statDivider} />
        <div className={styles.statItem}>
          <Wallet size={20} style={{ color: 'var(--warning-main)' }} />
          <span className={styles.statNumber}>{formatCurrency(stats.totaleImporto)}</span>
          <span className={styles.statText}>Totale Non Saldato</span>
        </div>
        <div className={styles.statDivider} />
        <div className={styles.statItem}>
          <ArrowUpCircle size={20} style={{ color: 'var(--success-main)' }} />
          <span className={styles.statNumber} style={{ color: 'var(--success-main)' }}>{formatCurrency(stats.daIncassare)}</span>
          <span className={styles.statText}>Da Incassare</span>
        </div>
        <div className={styles.statDivider} />
        <div className={styles.statItem}>
          <ArrowDownCircle size={20} style={{ color: 'var(--error-main)' }} />
          <span className={styles.statNumber} style={{ color: 'var(--error-main)' }}>{formatCurrency(stats.daPagare)}</span>
          <span className={styles.statText}>Da Pagare</span>
        </div>
      </div>

      {/* Overview Cards */}
      {overview && (
        <div className={styles.overviewGrid}>
          <div className={`${styles.overviewCard} ${styles.scaduteCard}`}>
            <div className={styles.cardIcon}>
              <AlertCircle size={24} />
            </div>
            <div className={styles.cardContent}>
              <h3>Scadute</h3>
              <p className={styles.cardCount}>{overview.scaduteCount}</p>
              <p className={styles.cardTotal}>{formatCurrency(overview.scaduteTotal)}</p>
            </div>
          </div>
          <div className={`${styles.overviewCard} ${styles.prossimeCard}`}>
            <div className={styles.cardIcon}>
              <Clock size={24} />
            </div>
            <div className={styles.cardContent}>
              <h3>In Scadenza (30gg)</h3>
              <p className={styles.cardCount}>{overview.prossimeCount}</p>
              <p className={styles.cardTotal}>{formatCurrency(overview.prossimeTotal)}</p>
            </div>
          </div>
          <div className={`${styles.overviewCard} ${styles.programmateCard}`}>
            <div className={styles.cardIcon}>
              <Calendar size={24} />
            </div>
            <div className={styles.cardContent}>
              <h3>Programmate</h3>
              <p className={styles.cardCount}>{overview.programmateCount}</p>
              <p className={styles.cardTotal}>{formatCurrency(overview.programmateTotal)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Scadute Section */}
      <div className={`${styles.section} ${styles.scaduteSection}`}>
        <div
          className={styles.sectionHeader}
          onClick={() => setScaduteExpanded(!scaduteExpanded)}
        >
          <div className={styles.sectionTitle}>
            <AlertCircle size={20} />
            <h2>Scadute</h2>
            <span className={styles.count}>{scadute.length}</span>
          </div>
          {scaduteExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        {scaduteExpanded && (
          <div className={styles.sectionContent}>
            {scadute.length === 0 ? (
              <p className={styles.emptyMessage}>Nessuna scadenza in ritardo</p>
            ) : (
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Scadenza</th>
                      <th>Tipo</th>
                      <th>Descrizione</th>
                      <th>Cliente/Categoria</th>
                      <th>Importo</th>
                      <th>Rata</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scadute.map(m => renderMovimentoRow(m, true))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Prossime Section */}
      <div className={`${styles.section} ${styles.prossimeSection}`}>
        <div
          className={styles.sectionHeader}
          onClick={() => setProssimeExpanded(!prossimeExpanded)}
        >
          <div className={styles.sectionTitle}>
            <Clock size={20} />
            <h2>In Scadenza (prossimi 30 giorni)</h2>
            <span className={styles.count}>{prossime.length}</span>
          </div>
          {prossimeExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        {prossimeExpanded && (
          <div className={styles.sectionContent}>
            {prossime.length === 0 ? (
              <p className={styles.emptyMessage}>Nessuna scadenza nei prossimi 30 giorni</p>
            ) : (
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Scadenza</th>
                      <th>Tipo</th>
                      <th>Descrizione</th>
                      <th>Cliente/Categoria</th>
                      <th>Importo</th>
                      <th>Rata</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prossime.map(m => renderMovimentoRow(m))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Programmate Section */}
      <div className={`${styles.section} ${styles.programmateSection}`}>
        <div
          className={styles.sectionHeader}
          onClick={() => setProgrammateExpanded(!programmateExpanded)}
        >
          <div className={styles.sectionTitle}>
            <Calendar size={20} />
            <h2>Pagamenti Programmati</h2>
            <span className={styles.count}>{programmate.length}</span>
          </div>
          {programmateExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        {programmateExpanded && (
          <div className={styles.sectionContent}>
            {programmate.length === 0 ? (
              <p className={styles.emptyMessage}>Nessun pagamento programmato</p>
            ) : (
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Scadenza</th>
                      <th>Tipo</th>
                      <th>Descrizione</th>
                      <th>Cliente/Categoria</th>
                      <th>Importo</th>
                      <th>Rata</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {programmate.map(m => renderMovimentoRow(m))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
